import { useCallback } from 'react';
import { useRouter } from 'expo-router';

import { type LastInteractedStore } from '../../../../context';
import { useCart } from '../../../../hooks';
import { type ProductForRestaurant } from '../../ProductDetails.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useProductDetailsNavigation = (params: Params) => {
  const { baseProductSlug, lastInteractedStore, productForRestaurant } = params;

  const deliveryAddressId = lastInteractedStore?.addressId;

  const router = useRouter();
  const { isCartEmpty } = useCart();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToLocationSearch = useCallback(() => {
    router.dismissTo('/menu/locations');
  }, [router]);

  const navigateToProductDetailsLocation = useCallback(() => {
    router.navigate({
      pathname: '/product-details/[baseProductSlug]/change-location',
      params: { baseProductSlug },
    });
  }, [baseProductSlug, router]);

  const navigateBack = useCallback(() => {
    router.back();
  }, [router]);

  const navigateToCustomization = useCallback(() => {
    const shouldSelectLocation = !lastInteractedStore || !productForRestaurant;

    if (shouldSelectLocation) {
      navigateToProductDetailsLocation();
      return;
    }

    if (deliveryAddressId) {
      router.navigate({
        pathname: '/delivery/[addressId]/[productSlug]',
        params: {
          productSlug: productForRestaurant?.slug ?? '',
          addressId: deliveryAddressId,
        },
      });
      return;
    }

    router.navigate({
      pathname: '/[restaurantSlug]/[productSlug]',
      params: {
        productSlug: productForRestaurant?.slug ?? '',
        restaurantSlug: productForRestaurant.restaurant.slug,
      },
    });
  }, [
    deliveryAddressId,
    lastInteractedStore,
    navigateToProductDetailsLocation,
    productForRestaurant,
    router,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    navigateBack,
    navigateToCustomization,

    // NOTE: If there is no need for reorder, we can open "inline" location search.
    navigateToLocationSearch: isCartEmpty
      ? navigateToProductDetailsLocation
      : navigateToLocationSearch,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  baseProductSlug: string;
  lastInteractedStore: LastInteractedStore | undefined;
  productForRestaurant: ProductForRestaurant | null | undefined;
};
